/**
 * Навигационный словарь Inbox — маппинг `reference_doctype`/`action_url` → URL портала.
 *
 * Контракт inbox §C.5, архитектура §6 (REQ-INB-007, INB §8). Маппинг — **клиентский**:
 * сервер отдаёт «сырые» `reference_*`; превращение в URL — ответственность UI
 * (концепция §14.6). При неизвестном DocType → fallback §8.2 (mark-read + раскрытие,
 * без перехода).
 *
 * Архитектура §6.3 (честность маппинга): **ровно один** известный DocType с рабочим
 * маршрут-целью в MVP — `PS Post` → `/post/<slug>`. Остальные (`HD Ticket`, `Lead`,
 * `PS News`, `Seminar`) присутствуют в словаре (для будущей активации), но
 * `status: 'absent'` → `buildUrl` возвращает `null` → fallback §8.2. Активация
 * будущих маршрутов — правкой словаря (без миграции данных).
 *
 * ARCH-SEC-INB-001 (defense-in-depth): `isSafeActionUrl` проверяет схему `action_url`
 * на клиенте, parity server-side `_validate_action_url` (`ps_message.py`).
 */

import type { PSMessage } from '@/lib/api/types';

export interface ReferenceRoute {
  /** DocType, для которого этот entry (match by reference_doctype). */
  doctype: string;
  /** Маршрут-цель на портале. Возвращает null, если маршрут отсутствует в MVP
   *  (status: 'absent'). */
  buildUrl: (referenceName: string) => string | null;
  /** Статус маршрута в MVP. */
  status: 'active' | 'absent';
}

/** Словарь известных DocType (INB §8.1 «Статус в MVP»). Readonly для forward-compat. */
export const REFERENCE_ROUTES: ReadonlyArray<ReferenceRoute> = [
  {
    doctype: 'PS Post',
    // ЕДИНСТВЕННЫЙ рабочий маршрут MVP (INB §8.1).
    buildUrl: (name) => (name ? `/post/${encodeURIComponent(name)}` : null),
    status: 'active',
  },
  {
    doctype: 'HD Ticket',
    // Маршрут отсутствует (портал не имеет detail-страницы тикета; /support — stub).
    buildUrl: () => null,
    status: 'absent',
  },
  {
    doctype: 'Lead',
    // CRM-сущности пока не реализованы на портале.
    buildUrl: () => null,
    status: 'absent',
  },
  {
    doctype: 'PS News',
    // Лента /news есть, но [slug]-маршрута нет в MVP.
    buildUrl: () => null,
    status: 'absent',
  },
  {
    doctype: 'Seminar',
    // /seminars — список без [slug]-детали в MVP.
    buildUrl: () => null,
    status: 'absent',
  },
];

/**
 * Резолвит кликабельную цель сообщения по приоритету:
 *  1. `action_url` (если задан и прошёл allow-list — mass-рассылки, REQ-INB-008)
 *  2. reference-routes mapping (`PS Post` → `/post/<slug>`, единственный активный)
 *  3. `null` (fallback §8.2: mark-read + раскрытие, без перехода)
 *
 * - Неизвестный DocType (нет в словаре) → null (AC-INB-007.2).
 * - Известный, но `status: 'absent'` → null (AC-INB-007.1a).
 * - Mass-рассылка с `action_url` → action_url (после allow-list, AC-INB-007.3).
 */
export function resolveMessageUrl(message: PSMessage): string | null {
  // 1. action_url имеет приоритет для mass-рассылок (reference_* = null у mass).
  if (message.action_url && isSafeActionUrl(message.action_url)) {
    return message.action_url;
  }
  // 2. reference-routes mapping (только PS Post активен в MVP).
  if (message.reference_doctype) {
    const route = REFERENCE_ROUTES.find(
      (r) => r.doctype === message.reference_doctype,
    );
    if (route && route.status === 'active') {
      return route.buildUrl(message.reference_name ?? '');
    }
  }
  // 3. fallback §8.2.
  return null;
}

/**
 * ARCH-SEC-INB-001: client-side allow-list для `action_url` (defense-in-depth).
 * Допускаются только http(s), protocol-relative `//host` и relative-URL
 * (`/`, `./`, `?`, `#` — без scheme). Любая другая схема → false →
 * `resolveMessageUrl` вернёт null → кликабельной цели нет (fallback §8.2).
 *
 * parity `accounting-calendar/parse-inline-markdown.ts::isAllowedUrl` (ARCH-SEC-003)
 * и server-side `_validate_action_url` (`architecture.md` §2.4).
 *
 * НЕ используется `new URL()`: он нормализует некоторые опасные формы
 * (`javascript:` в ряде контекстов) — проверяем scheme-префикс явно регэкспом.
 */
export function isSafeActionUrl(url: string): boolean {
  if (!url) return false;
  // Allow: http://, https://, //host (protocol-relative), /path, ./path, ?q, #anchor.
  return /^(?:https?:\/\/|\/\/|[/?#]|\.\/)/i.test(url.trim());
}
